import { env } from '@/app/config/env';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { MASTER_FALLBACK_ERROR } from '../constants/masters.constants';

/**
 * What the signed-in administrator is assigned to: their organizations, and their entities within each.
 *
 * <h2>Why this is its own read</h2>
 *
 * `/organizations` and `/legal-entities` answer what the *platform* holds. The Add form of a hierarchy master
 * needs what the *caller* holds, which is a different question with a different answer: an administrator
 * assigned to two of an organization's five entities may create business units under those two only.
 *
 * The data is Organization Setup step 5's — the "track assigned entity" rows — and the server derives the
 * caller from the token. There is deliberately no user parameter to pass: an endpoint that took one would let
 * anybody read anybody's assignments.
 *
 * Organizations come back ordered by name and the entities within each by name, so "the first organization"
 * the forms default to is the same one on every read.
 */

/** One legal entity the caller is assigned to. */
export interface AssignedEntity {
  /** The id a write takes; the name is only what a control shows. */
  id: number;
  name: string;
  code: string | null;
}

/** One organization the caller is assigned in, with their entities in it. */
export interface AssignedOrganization {
  id: number;
  /** The `ORG-001` code, for a caller matching against the session's. */
  code: string | null;
  name: string;
  entities: readonly AssignedEntity[];
}

export interface AssignedHierarchy {
  organizations: readonly AssignedOrganization[];
}

/** The wire shape; `AssignedHierarchyResponse` on the server. */
interface AssignedHierarchyDto {
  organizations?: readonly {
    id?: number | null;
    organizationCode?: string | null;
    name?: string | null;
    entities?: readonly {
      id?: number | null;
      entityName?: string | null;
      entityCode?: string | null;
    }[] | null;
  }[] | null;
}

const ENDPOINT = '/users/me/assigned-hierarchy';

/**
 * One organization and two of its entities, used when mocks are on.
 *
 * Two entities rather than one on purpose: an assignment that covers part of an organization is the case the
 * whole endpoint exists for, and a single-entity fixture cannot tell a scoped list from an unscoped one.
 */
const MOCK: AssignedHierarchy = {
  organizations: [
    {
      id: 1,
      code: 'ORG-001',
      name: 'AIT Global Procurement',
      entities: [
        { id: 1, name: 'AIT Global Pvt Ltd', code: 'ENT-001' },
        { id: 2, name: 'AIT Global FZE', code: 'ENT-002' },
      ],
    },
  ],
};

/** An organization or entity with no id or no name cannot be offered, so it is dropped rather than rendered blank. */
function toHierarchy(dto: AssignedHierarchyDto): AssignedHierarchy {
  const organizations: AssignedOrganization[] = [];

  for (const row of dto.organizations ?? []) {
    if (typeof row.id !== 'number' || !row.name) continue;

    const entities: AssignedEntity[] = [];
    for (const entity of row.entities ?? []) {
      if (typeof entity.id !== 'number' || !entity.entityName) continue;
      entities.push({ id: entity.id, name: entity.entityName, code: entity.entityCode ?? null });
    }

    organizations.push({
      id: row.id,
      code: row.organizationCode ?? null,
      name: row.name,
      entities,
    });
  }

  return { organizations };
}

/**
 * The read, de-duplicated across concurrent callers.
 *
 * The Organization field and the Entity control are two option sources drawn from one endpoint, and they are
 * requested together — so without this the same popup would issue the same request twice. Only concurrent
 * calls share; the promise is released as soon as it settles, so a later open re-reads.
 */
let inFlight: Promise<AssignedHierarchy> | null = null;

export async function fetchAssignedHierarchy(): Promise<AssignedHierarchy> {
  if (env.ENABLE_MOCKS) return MOCK;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const response = await apiClient.get<ApiEnvelope<AssignedHierarchyDto>>(ENDPOINT, {
      /* A refused read is the form's news, not grounds to end the session. */
      _allowForbidden: true,
    });
    return toHierarchy(unwrapEnvelope(response, MASTER_FALLBACK_ERROR));
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}
